<template>
  <div class="content-wrapper view-aptlist-manage p-0">
    <div class="content">
      <div class="card">
        <div class="card-body">
          <!-- Header -->
          <div class="d-flex align-items-center justify-content-between mb-3">
            <div class="d-flex align-items-center gap-2">
              <b-button
                variant="outline"
                size="sm"
                @click="handleBack"
                v-b-tooltip.hover
                title="ย้อนกลับ"
              >
                <i
                  class="fas fa-chevron-left"
                  style="font-size: 20px"
                ></i>
              </b-button>
              <h4 class="mb-0">ใบรับสินค้า</h4>
              <GrStatusBadge
                v-if="data"
                :status="data.status"
                :syncDt="data.syncDt"
              />
            </div>
            <div
              v-if="data && data.status === GR_STATUS.WAITING_HQ"
              class="d-flex gap-4"
            >
              <b-button
                variant="outline-danger"
                size="sm"
                class="btn-delete-adj"
                @click="handleDelete"
              >
                ยกเลิกรายการ
              </b-button>
              <b-button
                variant="danger"
                size="sm"
                class="btn-reject-adj"
                @click="$refs.actionModal.showReject()"
              >
                ปฏิเสธ
              </b-button>
              <b-button
                variant="primary"
                size="sm"
                class="btn-approve-adj"
                @click="$refs.actionModal.showApprove()"
              >
                อนุมัติ
              </b-button>
            </div>
          </div>

          <!-- Two-column layout -->
          <div
            v-if="!isLoading && data"
            class="gr-detail-layout"
          >
            <!-- Left: metadata -->
            <div class="gr-detail-info">
              <div class="info-row">
                <span class="info-label">เลขที่เอกสาร:</span>
                <span class="info-value">{{ data.grCode || "-" }}</span>
              </div>
              <div class="info-row">
                <span class="info-label">รหัสใบสั่งซื้ออ้างอิง:</span>
                <span class="info-value">{{ data.poRefCode || "-" }}</span>
              </div>
              <div
                v-if="data.files && data.files.length > 0"
                class="info-row gr-files-row"
              >
                <span class="info-label">ไฟล์ใบรับสินค้า:</span>
                <div class="gr-files-list">
                  <GrFileChip
                    v-for="(file, index) in data.files"
                    :key="file.id || index"
                    :filename="file.originalName"
                    :show-delete="false"
                    @click="previewFile(file.id)"
                  />
                </div>
              </div>
              <div class="info-row">
                <span class="info-label">รหัสผู้ขาย:</span>
                <span class="info-value">{{ data.vendorCode || "-" }}</span>
              </div>
              <div class="info-row">
                <span class="info-label">ชื่อผู้ขาย:</span>
                <span class="info-value">{{ data.vendorName || "-" }}</span>
              </div>
              <div class="info-row">
                <span class="info-label">ชื่อสาขา:</span>
                <span class="info-value">{{ data.branchName || "-" }}</span>
              </div>
              <div class="info-row">
                <span class="info-label">ต้องได้รับการอนุมัติจาก:</span>
                <span class="info-value">
                  <ApprovalSteps
                    :show-bm="true"
                    :show-hq="true"
                  />
                </span>
              </div>
              <div class="info-row">
                <span class="info-label">วันที่รับของ:</span>
                <span class="info-value">{{
                  data.receivedDt ? formatDate(data.receivedDt) : "-"
                }}</span>
              </div>
              <div class="info-row">
                <span class="info-label">วันที่ทำรายการ:</span>
                <span class="info-value">{{ formatDate(data.createdAt) }}</span>
              </div>
              <div
                v-if="showRejectReason"
                class="reject-reason-box"
              >
                <div class="reject-reason-title">เหตุผลที่ถูกปฏิเสธ:</div>
                <div>{{ data.rejectReason }}</div>
              </div>
            </div>

            <!-- Right: items table -->
            <div class="gr-detail-table">
              <b-table
                id="hqGrDetailTable"
                hover
                small
                responsive
                head-variant="light"
                :items="sortedItems"
                :fields="fields"
              >
                <template v-slot:cell(index)="row"
                  >{{ row.index + 1 }}.</template
                >
                <template v-slot:cell(image)="row">
                  <ProductImage :src="row.item.imageUrl || null" />
                </template>
                <template v-slot:cell(productCode)="row">
                  {{ row.item.productCode || "-" }}
                </template>
                <template v-slot:cell(productName)="row">
                  <span
                    v-if="row.item.productType === 'FREE'"
                    class="free-label"
                    >[ของแถม]</span
                  >
                  {{ row.item.productNameMain || "-" }}
                </template>
                <template v-slot:cell(quantity)="row">
                  {{ Number(row.item.quantity).toLocaleString() }}
                </template>
                <template v-slot:cell(uomName)="row">
                  {{ row.item.uomName || "-" }}
                </template>
                <template v-slot:cell(lotNumber)="row">
                  {{ row.item.lotNumber || "-" }}
                </template>
                <template v-slot:cell(expiredDt)="row">
                  {{
                    row.item.expiredDt ? formatDate(row.item.expiredDt) : "-"
                  }}
                </template>
              </b-table>
            </div>
          </div>

          <div
            v-if="isLoading"
            class="text-center py-5"
          >
            <b-spinner variant="primary"></b-spinner>
          </div>
        </div>
      </div>

      <HQApprovalActionModal
        ref="actionModal"
        item-type="GR"
        @approve="confirmApprove"
        @reject="confirmReject"
      />

      <Dialog ref="Dialog"></Dialog>
      <Loading v-if="isLoading"></Loading>
    </div>
  </div>
</template>

<script>
import Loading from "@/components/Loading";
import Dialog from "@/components/modal/Dialog.vue";
import ProductImage from "@/components/table/stock/component/ProductImage.vue";
import GrFileChip from "@/components/modal/ProductAndStock/GrFileChip.vue";
import GrStatusBadge from "@/components/badge/GrStatusBadge.vue";
import ApprovalSteps from "@/components/badge/ProductAndStock/ApprovalSteps.vue";
import HQApprovalActionModal from "@/components/ProductAndStock/HQApprovalActionModal.vue";
import { GR_STATUS } from "@/constants/productStock";
import { mapActions, mapMutations, mapGetters } from "vuex";
import { formatDate } from "@/utils/utils";

export default {
  name: "HQApprovalGRDetail",
  components: {
    Loading,
    Dialog,
    ProductImage,
    GrFileChip,
    GrStatusBadge,
    ApprovalSteps,
    HQApprovalActionModal,
  },
  beforeRouteEnter(to, from, next) {
    next(vm => {
      vm.fromRoute = from;
    });
  },
  data() {
    return {
      GR_STATUS,
      isLoading: false,
      fromRoute: null,
      data: null,
      fields: [
        {
          key: "index",
          label: "",
          thStyle: { width: "40px", minWidth: "40px" },
          class: "text-center",
        },
        {
          key: "image",
          label: "",
          thStyle: { width: "50px", minWidth: "50px" },
        },
        { key: "productCode", label: "รหัส", thStyle: { minWidth: "100px" } },
        {
          key: "productName",
          label: "ชื่อสินค้า",
          thStyle: { minWidth: "160px" },
        },
        {
          key: "quantity",
          label: "จำนวนสินค้า",
          thStyle: { minWidth: "100px" },
          class: "text-right",
        },
        {
          key: "uomName",
          label: "หน่วยสินค้า",
          thStyle: { minWidth: "100px" },
        },
        {
          key: "lotNumber",
          label: "เลขล็อต",
          thStyle: { minWidth: "100px" },
          class: "text-right",
        },
        {
          key: "expiredDt",
          label: "วันหมดอายุ",
          thStyle: { minWidth: "120px" },
          class: "text-right",
        },
      ],
    };
  },
  async mounted() {
    await this.loadData();
  },
  computed: {
    ...mapGetters({
      productList: "moduleProductInventory/getProductList",
    }),
    showRejectReason() {
      return (
        this.data?.status === GR_STATUS.REJECTED && !!this.data?.rejectReason
      );
    },
    sortedItems() {
      const items = this.data?.items || [];
      const withImage = items.map(item => ({
        ...item,
        imageUrl:
          this.productList.find(p => p.productId === item.productId)
            ?.imageUrl || null,
      }));
      return [
        ...withImage.filter(i => i.productType !== "FREE"),
        ...withImage.filter(i => i.productType === "FREE"),
      ];
    },
  },
  methods: {
    ...mapActions({
      fetchGoodsReceiveById: "moduleGoodsReceive/fetchGoodsReceiveById",
      fetchProductList: "moduleProductInventory/fetchProductList",
      previewMediaFile: "moduleGoodsReceive/previewMediaFile",
      hqApproveGoodsReceiveAction: "moduleGoodsReceive/hqApproveGoodsReceive",
      deleteGoodsReceiveAction: "moduleGoodsReceive/deleteGoodsReceive",
      fetchHQStockAdjustmentList:
        "moduleProductInventory/fetchHQStockAdjustmentList",
    }),
    ...mapMutations({
      patchHQGoodsReceiveStatus: "moduleGoodsReceive/patchHQGoodsReceiveStatus",
      removeHQGoodsReceive: "moduleGoodsReceive/removeHQGoodsReceive",
    }),
    async loadData() {
      this.isLoading = true;
      try {
        const { clinicUrl, branch } = this.$route.params;
        if (!this.productList.length) {
          await this.fetchProductList({ clinicUrl, branchUrl: branch });
        }
        await this.loadDetail();
      } catch (err) {
        const errorMessage = err.response?.data?.errorMessage;
        this.$refs.Dialog.showToast(
          "danger",
          typeof errorMessage === "string"
            ? errorMessage
            : "โหลดข้อมูลไม่สำเร็จ กรุณาลองใหม่"
        );
        this.goBack();
      } finally {
        this.isLoading = false;
      }
    },
    async loadDetail() {
      const { clinicUrl, branch, grId } = this.$route.params;
      const detail = await this.fetchGoodsReceiveById({
        clinicUrl,
        branchUrl: branch,
        id: grId,
      });
      if (!detail) {
        this.$refs.Dialog.showToast("danger", "รายการนี้ถูกลบไปแล้ว");
        setTimeout(() => this.goBack(), 1500);
        return;
      }
      this.data = detail;
    },
    async confirmApprove() {
      this.isLoading = true;
      try {
        const { clinicUrl, branch, grId } = this.$route.params;
        const res = await this.hqApproveGoodsReceiveAction({
          clinicUrl,
          branchUrl: branch,
          id: grId,
          action: "APPROVE",
        });
        this.$refs.Dialog.showToast("success", "อนุมัติใบรับสินค้าสำเร็จ อัปเดตสต๊อกแล้ว");
        const updatedStatus = res?.data?.data?.status;
        if (updatedStatus) {
          this.data = { ...this.data, status: updatedStatus };
          this.patchHQGoodsReceiveStatus({
            id: Number(grId),
            status: updatedStatus,
          });
        } else {
          await this.fetchHQStockAdjustmentList({ clinicUrl });
          await this.loadDetail();
        }
      } catch (err) {
        const errorMessage = err.response?.data?.errorMessage;
        this.$refs.Dialog.showToast(
          "danger",
          typeof errorMessage === "string"
            ? errorMessage
            : "อนุมัติใบรับสินค้าไม่สำเร็จ กรุณาลองใหม่"
        );
      } finally {
        this.isLoading = false;
      }
    },
    async confirmReject(rejectReason) {
      this.isLoading = true;
      try {
        const { clinicUrl, branch, grId } = this.$route.params;
        const res = await this.hqApproveGoodsReceiveAction({
          clinicUrl,
          branchUrl: branch,
          id: grId,
          action: "REJECT",
          rejectReason: rejectReason || undefined,
        });
        this.$refs.Dialog.showToast("success", "ปฏิเสธใบรับสินค้าสำเร็จ");
        const updatedStatus = res?.data?.data?.status;
        if (updatedStatus) {
          this.data = {
            ...this.data,
            status: updatedStatus,
            rejectReason: rejectReason || null,
          };
          this.patchHQGoodsReceiveStatus({
            id: Number(grId),
            status: updatedStatus,
          });
        } else {
          await this.fetchHQStockAdjustmentList({ clinicUrl });
          await this.loadDetail();
        }
      } catch (err) {
        const errorMessage = err.response?.data?.errorMessage;
        this.$refs.Dialog.showToast(
          "danger",
          typeof errorMessage === "string"
            ? errorMessage
            : "ปฏิเสธใบรับสินค้าไม่สำเร็จ กรุณาลองใหม่"
        );
      } finally {
        this.isLoading = false;
      }
    },
    async handleDelete() {
      const result = await this.$refs.Dialog.showAlertConfirm(
        "ยกเลิกใบรับสินค้า ?",
        "คุณต้องการยกเลิกใบรับสินค้านี้ใช่หรือไม่"
      );
      if (!result.value) return;
      this.isLoading = true;
      try {
        const { clinicUrl, branch, grId } = this.$route.params;
        const res = await this.deleteGoodsReceiveAction({
          clinicUrl,
          branchUrl: branch,
          id: grId,
        });
        this.$refs.Dialog.showToast("success", "ยกเลิกใบรับสินค้าสำเร็จ");
        const updatedStatus = res?.data?.data?.status;
        if (updatedStatus) {
          this.data = { ...this.data, status: updatedStatus };
          this.patchHQGoodsReceiveStatus({
            id: Number(grId),
            status: updatedStatus,
          });
        } else {
          this.removeHQGoodsReceive({ id: Number(grId) });
          setTimeout(() => this.goBack(), 1000);
        }
      } catch (err) {
        const errorMessage = err.response?.data?.errorMessage;
        this.$refs.Dialog.showToast(
          "danger",
          typeof errorMessage === "string"
            ? errorMessage
            : "ยกเลิกใบรับสินค้าไม่สำเร็จ กรุณาลองใหม่"
        );
      } finally {
        this.isLoading = false;
      }
    },
    async previewFile(id) {
      const { clinicUrl, branch } = this.$route.params;
      const result = await this.previewMediaFile({
        clinicUrl,
        branchUrl: branch,
        id,
      });
      if (result.success) return;
      if (result.reason === "not_found")
        this.$refs.Dialog.showToast("danger", "ไม่พบไฟล์");
      else if (result.reason === "error")
        this.$refs.Dialog.showToast("danger", "เปิดไฟล์ไม่สำเร็จ");
    },
    formatDate(value) {
      return formatDate(value);
    },
    handleBack() {
      if (this.fromRoute?.name) {
        this.$router.go(-1);
      } else {
        this.goBack();
      }
    },
    goBack() {
      this.$router.push({
        name: "HQApproval",
        params: { clinicUrl: this.$route.params.clinicUrl },
      });
    },
  },
};
</script>

<style scoped>
.gap-2 {
  gap: 8px;
}

.gap-4 {
  gap: 16px;
}

.gr-detail-layout {
  display: flex;
  gap: 24px;
  border: 1px solid #dee2e6;
  padding: 20px;
}

.gr-detail-info {
  flex: 0 0 30%;
  min-width: 0;
  border-right: 1px solid #dee2e6;
  padding-right: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.gr-detail-table {
  flex: 1;
  min-width: 0;
  overflow-x: auto;
}

.info-row {
  line-height: 1.6;
  display: flex;
}

.info-label {
  font-weight: 600;
  color: #000;
  white-space: nowrap;
  flex-shrink: 0;
}

.info-value {
  margin-left: 4px;
  word-break: break-word;
  min-width: 0;
  flex: 1;
}

.free-label {
  margin-right: 4px;
  color: #212529;
}

.reject-reason-box {
  background: #ffd9dd;
  border-radius: 4px;
  padding: 16px;
}

.reject-reason-title {
  font-weight: 600;
  font-size: 16px;
  color: #dd3333;
  margin-bottom: 4px;
}

.gr-files-row {
  flex-direction: column;
  gap: 8px;
}

.gr-files-list {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.btn-approve-adj {
  padding: 8px 50px;
  font-size: 14px;
  font-weight: 600;
}

.btn-delete-adj {
  padding: 8px 30px;
  font-size: 14px;
  font-weight: 600;
  border-color: #dd3333;
  color: #dd3333;
}

.btn-delete-adj:hover {
  background-color: #dd3333;
  color: #fff;
}

.btn-reject-adj {
  padding: 8px 50px;
  font-size: 14px;
  font-weight: 600;
  background-color: #dd3333;
  border-color: #dd3333;
  color: #fff;
}

.btn-reject-adj:hover {
  background-color: #bb2222;
  border-color: #bb2222;
  color: #fff;
}

::v-deep #hqGrDetailTable thead th,
::v-deep #hqGrDetailTable tbody td {
  vertical-align: middle !important;
}

@media (max-width: 768px) {
  .gr-detail-layout {
    flex-direction: column;
  }
  .gr-detail-info {
    flex: none;
    border-right: none;
    padding-right: 0;
    border-bottom: 1px solid #dee2e6;
    padding-bottom: 16px;
  }
}
</style>
